/**
 * @file This file contains the classes implementing the interfaces defined in the corresponding types file.
 */

import type { Definition } from 'typescript-json-schema';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import type { PropertyPath } from '@sap-ux/vocabularies-types';
import i18next from 'i18next';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { ControlType, SchemaKeyName, SchemaTag } from '@sap/ux-specification-types';
import type {
    GenerateAppSchemaParameters,
    ProcessingRule,
    SchemaHandlingParams,
    SyncRule,
    ValidityInformation
} from '@sap/ux-specification-types';
import {
    addEnumForValidAnnotations,
    DEFINITION_LINK_PREFIX,
    determineDataType,
    getLabelForPropertyPath,
    prepareRef
} from '../../../common';
import { descriptionSrcURL, syncRule, validity } from '../../../common/decoration';
import type * as v2 from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/FilterBar';
import type { LRSchemaHandlingParams } from '../pages/ListReportNew';
import { getManifestPathToPageSettings, getManifestPropertyByPath } from '../../utils';
import type { ManifestRule } from '../../generate/manifestPropertyUtils';
import {
    getSyncRuleForManifestProperty,
    standardSyncRuleForManifestSettingsProperty
} from '../../generate/manifestPropertyUtils';

/**
 * Validity information for properties connected to the date range feature.
 */
const dateRangeValidity: ValidityInformation = {
    since: '1.77.0'
};

/* Begin: Types being used for schemaHandlingParams.specificParams */

/**
 * An instance of this type collects information about a field in a filter bar.
 * Instances are created in processingRuleAdapter of class ListReportFilterBar (one for each selection field).
 *
 * @property field - the original annotation object for the field
 * @property dataType - the EDM data type of the field
 * @property hasDateRangeOption - whether the field can potentially be displayed with date range options
 * @property position - the position of the field in the filter bar
 */
type SelectionFieldInfo = {
    field: object;
    dataType: string;
    hasDateRangeOption: boolean;
    position: number;
};

/**
 * An instance of this type collects the information about a filter bar.
 * Instances are created in processingRuleAdapter of class ListReportFilterBar and passed as specific params to the next processing level.
 *
 * @property entityType - the entity type definition of the entity set the filter bar is defined for
 * @property fieldDefinitions - the filter fields
 * @property hasFieldWithDateRangeOption - whether the filter bar contains at least one field that can potentially be displayed with date range options
 */
type FilterBarInfo = {
    entityType: Edm.EntityType;
    fieldDefinitions: { [key: string]: SelectionFieldInfo };
    hasFieldWithDateRangeOption: boolean;
};

/**
 * An instance of this type collects information about a field in a filter bar.
 * Compared to SelectionFieldInfo it also contains context information relevant for the field.
 * Instances are created in processingRuleAdapter of class FilterFields (one for each selection field).
 *
 * @property accessor - the accessor used to identify this field within all fields of the filter bar within the manifest
 * @property selectionFieldInfo - the selection field information for this field
 * @property entityType - the entity type from the filter bar the field resides in
 */
type FilterFieldInfo = {
    accessor: string; // the accessor used to identify this field within all fields of the filter bar within the manifest
    selectionFieldInfo: SelectionFieldInfo;
    entityType: Edm.EntityType; // the entity type from the filter bar the field resides in
};

/* End: Types being used for schemaHandlingParams.specificParams */

/**
 * Common syncRule for all properties of the filter bar with artifactType FlexChange.
 */
const syncRuleForFlexSmartFilterBar = {
    flex: {
        controlType: (): ControlType => ControlType.SmartFilterBar
    },
    processingRuleAdapter(processingRule: ProcessingRule): void {
        processingRule.controlId = 'listReportFilter';
    }
};

/**
 * This function generates a sync rule for a manifest based property specifying the behavior of filter fields that can potentially be displayed with date range options.
 * Note that these properties are only relevant when at least one filter field has this option.
 *
 * @param property - name of the manifest based property
 * @returns a SyncRule for the specified property
 */
function getSyncRuleForDateRangeProperty(property: string): SyncRule {
    return getSyncRuleForManifestProperty(
        property,
        // analyze:
        function (element: Definition, schemaHandlingParams: SchemaHandlingParams): ManifestRule {
            // As described in the syncRule of the dateSettings property of class CommonFilterBar below,
            // we currently enable the date range properties unconditionally, since the exact condition when the filter bar contains a potential date range field is not yet implemented.
            // Therefore, the following code is temporarily disabled.
            /*            const filterBarInfo = schemaHandlingParams.specificParams as FilterBarInfo;
            // If the filter bar has no date fields, we have a reason why this property is disallowed:
            const disallowedReason = filterBarInfo.hasFieldWithDateRangeOption ? undefined : i18next.t('NODATEFIELD'); */
            const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                'filterSettings',
                'dateSettings'
            ]);
            return {
                //                disallowedReason,
                manifestPath
            };
        } // analyze
    );
}

export class DateRange implements v2.DateRange {
    @syncRule(getSyncRuleForDateRangeProperty('useDateRange'))
    @validity(dateRangeValidity)
    useDateRange?: boolean;

    @syncRule(getSyncRuleForDateRangeProperty('selectedValues'))
    @validity(dateRangeValidity)
    selectedValues?: v2.DateRangeEnum[];

    @syncRule(getSyncRuleForDateRangeProperty('exclude'))
    @validity(dateRangeValidity)
    exclude?: boolean;
}

/**
 * Sync rule for properties that are only relevant for filter fields that can potentially be displayed with date range options.
 */
const onlyDateFieldsPropertySyncRule: SyncRule = {
    manifest: {},
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const filterFieldInfo = schemaHandlingParams.specificParams as FilterFieldInfo;
        if (!filterFieldInfo.selectionFieldInfo.hasDateRangeOption) {
            delete processingRule.element;
            return;
        }
        processingRule.manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
            'filterSettings',
            'dateSettings',
            'fields',
            filterFieldInfo.accessor
        ]);
    }
};

export class DefaultDateRange implements v2.DefaultDateRange {
    operation: v2.DefaultDateRangeValueEnum;
}

// FilterField
// Note that filter fields only have manifest properties and these properties are only relevant when they can potentially be displayed with date range options
@syncRule({
    // Task of this function is to add information to the field definition that is not already part of the generic schema (because it is specific for this field)
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const filterFieldInfo = schemaHandlingParams.specificParams as FilterFieldInfo;
        const selectionField = filterFieldInfo.selectionFieldInfo.field;
        const entityTypeDefinition = filterFieldInfo.entityType;
        const value = selectionField['value'];
        const description =
            selectionField['type'] === 'PropertyPath'
                ? getLabelForPropertyPath(selectionField as PropertyPath, entityTypeDefinition)
                : value;
        processingRule.element.description = description;
        processingRule.element[SchemaTag.dataType] = filterFieldInfo.selectionFieldInfo.dataType;
        processingRule.element[SchemaTag.keys] = [{ name: SchemaKeyName.value, value: `${value}` }];
    }
})
export class FilterField implements v2.FilterField {
    // selectedValues
    @syncRule(onlyDateFieldsPropertySyncRule)
    @validity(dateRangeValidity)
    selectedValues?: v2.DateRangeEnum[];

    // exclude
    @syncRule(onlyDateFieldsPropertySyncRule)
    @validity(dateRangeValidity)
    exclude?: boolean;

    // customDateRangeImplementation
    @syncRule(onlyDateFieldsPropertySyncRule)
    @validity(dateRangeValidity)
    customDateRangeImplementation?: string;

    // filter
    @syncRule(onlyDateFieldsPropertySyncRule)
    @validity(dateRangeValidity)
    filter?: v2.FilterType[];

    // defaultValue
    @syncRule(onlyDateFieldsPropertySyncRule)
    @validity({
        since: '1.90.0'
    })
    defaultValue?: DefaultDateRange;
}

// FilterFields
@syncRule({
    // Task of this function is to process the list of filter fields (provided in the FilterBarInfo) and transform them into properties
    // of processingRule.element. Moreover, the further processing of these child properties is prepared by creating a suitable ProcessingRulesAdapter.
    processingRuleAdapter(
        processingRule: ProcessingRule,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ): void {
        const filterBarInfo = schemaHandlingParams.specificParams as FilterBarInfo;
        const fieldDefinitions = filterBarInfo.fieldDefinitions;
        let properties: {
            [key: string]: Definition;
        };
        for (const accessor in fieldDefinitions) {
            const fieldDefinition = fieldDefinitions[accessor];
            // initialize schema representation of the filter field with a link to the generic type definition, adaptation to a specific type will be ensured below:
            const propertyDefinition = {
                $ref: DEFINITION_LINK_PREFIX + 'FilterField'
            } as Definition;
            propertyDefinition[SchemaTag.propertyIndex] = fieldDefinition.position;
            properties = properties || {};
            properties[accessor] = propertyDefinition;
        }
        processingRule.element.properties = properties;
        delete processingRule.element.additionalProperties;

        const processingRulesAdapter = function (property: string, processingRule: ProcessingRule): void {
            const filterFieldInfo: FilterFieldInfo = {
                accessor: property,
                selectionFieldInfo: fieldDefinitions[property],
                entityType: filterBarInfo.entityType
            };
            processingRule.referenceAdaptation = {
                suffix: prepareRef(property), // ensure that a specific copy of type FilterField will be created in the specific schema
                syncRuleProvider: FilterField,
                specificParams: filterFieldInfo // Pass the information about the filter field to the next processing level
            };
        };
        processingRule.referenceAdaptation = {
            processingRulesAdapter
        };
    }
})
export class FilterFields implements v2.FilterFields {
    [key: string]: v2.FilterField;
}

class CommonFilterBar implements v2.CommonFilterBar {
    // liveMode
    @syncRule(syncRuleForFlexSmartFilterBar)
    liveMode?: boolean;

    // smartVariantManagement
    @syncRule(standardSyncRuleForManifestSettingsProperty)
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/e3b12f41d70c442f9d4cc2e7722bbb17')
    smartVariantManagement?: boolean;

    // variantManagementHidden
    @syncRule(standardSyncRuleForManifestSettingsProperty)
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/e3b12f41d70c442f9d4cc2e7722bbb17')
    variantManagementHidden: boolean;

    // subTitleIfVariantMgmtHidden
    @syncRule(standardSyncRuleForManifestSettingsProperty)
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/e3b12f41d70c442f9d4cc2e7722bbb17')
    subTitleIfVariantMgmtHidden?: string;

    // showClearOnFB
    @syncRule(syncRuleForFlexSmartFilterBar)
    showClearOnFB?: boolean;

    // showFilterConfiguration
    @syncRule(syncRuleForFlexSmartFilterBar)
    showFilterConfiguration?: boolean;

    // showRestoreOnFB
    @syncRule(syncRuleForFlexSmartFilterBar)
    showRestoreOnFB?: boolean;

    // dateSettings
    @syncRule({
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            // Preferably, the dateSettings property should only be visible when there is at least one potential date range field in the filter bar, since otherwise it is meaningless.
            // However, the exact condition when a field is a potential date range field is currently not implemented.
            // Moreover, it needs to be checked whether we should also consider fields not belonging to the filter bar, but potentially added by end-users via the 'Adapt filters' button.
            // Therefore, the following logic is temporarily disabled. Like in the 'old' implementation we simply provide the property unconditionally.
            /*            const filterBarInfo = schemaHandlingParams.specificParams as FilterBarInfo;
            if (!filterBarInfo.hasFieldWithDateRangeOption) {
                const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                    'filterSettings'
                ]);
                const { hasPath } = getManifestPropertyByPath(
                    generateParameters.manifest,
                    manifestPath,
                    'dateSettings'
                );
                // If the filter bar does not contain any date fields and there are no date settings in the manifest yet: Do not bother user with meaningless date settings.
                // Possible future improvement: Add a warning message when date settings are present but meaningless because there is no date field.
                if (!hasPath) {
                    delete processingRule.element;
                    return;
                }
            } */
            processingRule.referenceAdaptation = {
                syncRuleProvider: DateRange
            };
        }
    })
    @validity(dateRangeValidity)
    dateSettings?: DateRange;

    // selectionFields
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule): void {
            processingRule.referenceAdaptation = {
                syncRuleProvider: FilterFields
            };
        }
    })
    selectionFields?: FilterFields;
}

// ListReportFilterBar
@syncRule({
    // The task of this function is to create an instance of type FilterBarInfo and pass it to the next processing level.
    // Moreover, the icon to be used for this artifact im Page Map is specified.
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        processingRule.element[SchemaTag.icon] = 'Filter'; // do not use member of enum UiIcons in @sap-ux/ui-components to avoid unwanted dependency
        const lrParams = schemaHandlingParams.specificParams as LRSchemaHandlingParams;
        // Collect selection fields (the fields belonging to the filter bar) and thereby determine whether there is at least one field that can potentially be displayed with date range options
        const selectionFieldAnnotation = lrParams.uIAnnotations?.SelectionFields ?? [];
        const fieldDefinitions = {} as { [key: string]: SelectionFieldInfo };
        let hasFieldWithDateRangeOption: boolean = false;
        selectionFieldAnnotation.forEach((selectionField, i) => {
            const dataType = determineDataType(selectionField);
            // The following logic is identical to the 'old' logic as implemented in function addSelectionFields of class v2/generate/utils.ts.
            // However, this logic seems to deviate from the logic implemented in function isDateRange in class semanticDateRangeHelper of Fiori Elements runtime.
            // Therefore, we may need to adapt the logic as soon as the exact condition when a field is a potential date range field is clarified.
            const hasDateRangeOption = ['Date', 'DateTime', 'DateTimeOffset'].includes(dataType);
            hasFieldWithDateRangeOption = hasFieldWithDateRangeOption || hasDateRangeOption;
            fieldDefinitions[selectionField.value] = {
                field: selectionField,
                dataType,
                hasDateRangeOption,
                position: i
            };
        });
        const specificParams: FilterBarInfo = {
            entityType: lrParams.entityType,
            fieldDefinitions,
            hasFieldWithDateRangeOption
        };
        processingRule.referenceAdaptation = {
            specificParams
        };
    }
})
export class ListReportFilterBar extends CommonFilterBar implements v2.ListReportFilterBar {
    // annotationPath
    // This property is manifest based and possesses a value list
    @syncRule({
        manifest: {},
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const validAnnotations = [
                UIAnnotationTerms.SelectionPresentationVariant,
                UIAnnotationTerms.SelectionVariant,
                UIAnnotationTerms.PresentationVariant
            ];
            const filterBarInfo = schemaHandlingParams.specificParams as FilterBarInfo;
            // set the list of possible values:
            addEnumForValidAnnotations(filterBarInfo.entityType, validAnnotations, processingRule.element);
            // set the manifest path for the property:
            const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath);
            processingRule.manifestPath = manifestPath;
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/22e32b7241e843c1a84fee142e9d8ef0')
    @validity({
        since: '1.86.0'
    })
    annotationPath?: string;
}
